import React, { useState, useEffect } from "react";
import Back from "../components/Common/Back";
import { useSelector, useDispatch } from "react-redux";
import DeliveryForm from "../components/PlaceOrder/DeliveryForm";
import OrderCard from "../components/PlaceOrder/OrderCard";
import OrderPrice from "../components/PlaceOrder/OrderPrice";
import { clearCartOnOrder } from "../redux/menu/cartSlice";
import { fetchProfile } from "../redux/menu/ProfileSlice";
import { isStoreOpen } from "../utils/storeTimeUtils";
import useTheme from "../hooks/useTheme";
import useBackgroundImage from "../hooks/useBackgroundImage";
import { getFullScreenBackgroundStyle } from "../utils/backgroundStyles";

export default function Cart() {
  const order = useSelector((state) => state.cart.cart);
  const { profile } = useSelector((state) => state.profile);
  const profileStatus = useSelector((state) => state.profile.status);
  const dispatch = useDispatch();
  const theme = useTheme();
  const backgroundImage = useBackgroundImage();
  
  // Check if store is currently open
  const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
  
  // Check if store status is available (not false)
  const storeStatusAvailable = profile?.status !== false;
  
  // Store is available for orders if it's open and status is available
  const storeAvailable = storeIsOpen && storeStatusAvailable;

  // Helper function to format price - remove .00 if decimal part is 00
  const formatPrice = (price) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    const formattedPrice = numericPrice.toFixed(2);
    return formattedPrice.replace('.00', '');
  };
  // Helper function to get delivery details from localStorage
  const getDeliveryDetailsFromStorage = () => {
    try {
      const deliveryData = localStorage.getItem("deliveryDetails");
      if (deliveryData) {
        const parsed = JSON.parse(deliveryData);
        return {
          name: parsed.name || "",
          building: parsed.building || "",
          area: parsed.area || "",
          mobile: parsed.mobile || "",
          kitchenNotes: parsed.kitchenNotes || "",
          orderType: parsed.orderType || "delivery", // Default to delivery
          useCurrentLocation: parsed.useCurrentLocation !== undefined ? parsed.useCurrentLocation : true, // Default to true
          estimatedTime: parsed.estimatedTime || "",
          vehicleNumber: parsed.vehicleNumber || "",
          paymentMethod: parsed.paymentMethod || "cash_on_delivery", // Default to cash on delivery
        };
      }
    } catch (error) {
      console.error("Error loading delivery details from localStorage:", error);
    }
    return {
      name: "",
      building: "",
      area: "",
      mobile: "",
      kitchenNotes: "",
      orderType: "delivery", // Default to delivery
      useCurrentLocation: true, // Default to true
      estimatedTime: "",
      vehicleNumber: "",
      paymentMethod: "cash_on_delivery", // Default to cash on delivery
    };
  };

  // Helper function to save delivery details to localStorage
  const saveDeliveryDetailsToStorage = (deliveryDetails) => {
    try {
      localStorage.setItem("deliveryDetails", JSON.stringify(deliveryDetails));
    } catch (error) {
      console.error("Error saving delivery details to localStorage:", error);
    }
  };

  const [state, setState] = useState({
    form: getDeliveryDetailsFromStorage(),
  });
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    isLocationFetched: false
  });
  const { form } = state;
  const changeHandler = (event) => {
    const { name, value, type, checked } = event.target;
    
    // Special handling for location checkbox
    if (name === 'useCurrentLocation' && checked) {
      // User is trying to enable location, request permission again
      requestLocationPermission();
      return; // Don't update state yet, wait for permission result
    }
    
    setState((prev) => {
      const updatedForm = {
        ...prev.form,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Save to localStorage whenever form changes
      saveDeliveryDetailsToStorage(updatedForm);
      
      return {
        ...prev,
        form: updatedForm,
      };
    });
  };

  // Function to request location permission when user manually enables location
  const requestLocationPermission = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Permission granted - enable location
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            isLocationFetched: true
          });
          
          // Update form to enable location
          const updatedForm = {
            ...form,
            useCurrentLocation: true
          };
          setState(prev => ({
            ...prev,
            form: updatedForm
          }));
          saveDeliveryDetailsToStorage(updatedForm);
        },
        (error) => {
          console.log("Location permission denied:", error);
          
          // Permission denied - keep location disabled
          setLocation(prev => ({ ...prev, isLocationFetched: true }));
          
          // Ensure location remains disabled
          const updatedForm = {
            ...form,
            useCurrentLocation: false
          };
          setState(prev => ({
            ...prev,
            form: updatedForm
          }));
          saveDeliveryDetailsToStorage(updatedForm);
          
          // Show user feedback for denied permission
          alert("Location permission is required to use this feature. Please enable location access in your browser settings and try again.");
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0 // Don't use cached location, get fresh permission
        }
      );
    } else {
      // Geolocation not supported - keep location disabled
      const updatedForm = {
        ...form,
        useCurrentLocation: false
      };
      setState(prev => ({
        ...prev,
        form: updatedForm
      }));
      saveDeliveryDetailsToStorage(updatedForm);
      
      // Show user feedback for unsupported browser
      alert("Your browser doesn't support location services. Please use a different browser or enter your address manually.");
    }
  };
  // Check if all required form fields are filled (all fields are now optional)
  // For takeaway, no required fields
  // For delivery, building and area are required
  // For drive_through, no required fields
  const isFormComplete = form.orderType === "takeaway" 
    ? true // No required fields for takeaway
    : form.orderType === "drive_through"
    ? true // No required fields for drive_through
    : form.orderType === "delivery"
    ? (form.building.trim() !== "" && form.area.trim() !== "")
    : false; // If orderType is not recognized, form is incomplete
  
  // Button is disabled if form is incomplete OR store is not available
  const disabled = !isFormComplete || !storeAvailable;
  // Initialize location state and fetch location if useCurrentLocation is already true
  useEffect(() => {
    setLocation(prev => ({ ...prev, isLocationFetched: true }));
    
    // If useCurrentLocation is already true (from localStorage), fetch location immediately
    if (form.useCurrentLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Permission granted - update location
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              isLocationFetched: true
            });
          },
          (error) => {
            console.log("Location permission denied on page load:", error);
            // Permission denied - keep location disabled
            setLocation(prev => ({ ...prev, isLocationFetched: true }));
            
            // Update form to disable location
            const updatedForm = {
              ...form,
              useCurrentLocation: false
            };
            setState(prev => ({
              ...prev,
              form: updatedForm
            }));
            saveDeliveryDetailsToStorage(updatedForm);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes - use cached location if available
          }
        );
      }
    }
  }, []);

  // Auto-select first available order type when profile loads
  useEffect(() => {
    if (profile && form.orderType === "delivery") {
      // Check if current order type is still available, if not select first available
      const availableOrderTypes = [];
      if (profile.enable_delivery) availableOrderTypes.push("delivery");
      if (profile.enable_takeaway) availableOrderTypes.push("takeaway");
      if (profile.enable_drive) availableOrderTypes.push("drive_through");
      
      if (availableOrderTypes.length > 0 && !availableOrderTypes.includes(form.orderType)) {
        const updatedForm = {
          ...form,
          orderType: availableOrderTypes[0]
        };
        setState(prev => ({
          ...prev,
          form: updatedForm
        }));
        saveDeliveryDetailsToStorage(updatedForm);
      }
    }
  }, [profile]);

  // Fetch profile data on component mount
  useEffect(() => {
    if (profileStatus === 'idle') {
      dispatch(fetchProfile());
    }
  }, [dispatch, profileStatus]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sendMessage = () => {
    let messages = "🛒 *NEW ORDER*\n\n";
    
    // Get phone number from profile, fallback to default if not available
    let phoneNumber = profile.whatsapp; // Default fallback
    
    
    let subtotal = 0;
    let currency = profile?.currency || "AED";
    let totalItems = 0;
    
    // Add order header
    messages += "📋 *ORDER DETAILS*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n\n";
    
    // Construct the message with detailed product information
    order.forEach((element, index) => {
      const itemTotal = element.price * element.quantity;
      subtotal += itemTotal;
      totalItems += element.quantity;
      
      messages += `*${index + 1}. ${element.name || element.title}*\n`;
      
      // Add product code only in WhatsApp message (not shown elsewhere)
      if (element.code != null && element.code !== '') {
        messages += `   Code: ${element.code}\n`;
      }
      
      // Add variant info if available
      if (element.variant && element.variant.name && element.variant.name.trim() !== '') {
        messages += `   Size: ${element.variant.name}\n`;
      }
      
      // Add veg/non-veg indicator
      if (element.is_veg !== undefined) {
        messages += `   Type: ${element.is_veg ? '🟢 Veg' : '🔴 Non-Veg'}\n`;
      }
      
      messages += `   Price: ${currency} ${formatPrice(element.price)}\n`;
      messages += `   Quantity: ${element.quantity}\n`;
      messages += `   Subtotal: ${currency} ${formatPrice(itemTotal)}\n\n`;
    });

    // Calculate delivery charge
    const deliveryCharge = profile?.delivery_charge !== null && profile?.delivery_charge !== undefined 
      ? parseFloat(profile.delivery_charge) 
      : null;
    const totalAmount = subtotal + (deliveryCharge !== null && deliveryCharge > 0 ? deliveryCharge : 0);

    // Add order summary
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    messages += `*Total Items: ${totalItems}*\n`;
    messages += `*Subtotal: ${currency} ${formatPrice(subtotal)}*\n`;
    
    // Add delivery charge to message for delivery orders (only if delivery_charge is not null)
    if (form.orderType === 'delivery' && deliveryCharge !== null) {
      if (deliveryCharge > 0) {
        messages += `*Delivery Charge: ${currency} ${formatPrice(deliveryCharge)}*\n`;
      } else if (deliveryCharge === 0) {
        messages += `*Delivery Charge: Free Delivery*\n`;
      } else {
        messages += `*Delivery Charge: Applicable*\n`;
      }
    }
    
    messages += `*Total Amount: ${currency} ${formatPrice(totalAmount)}*\n\n`;
    
    // Add customer details
    messages += "👤 *CUSTOMER DETAILS*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    if (form.orderType !== 'drive_through' && form.name.trim() !== "") {
      messages += `Name: ${form.name}\n`;
    }
    messages += `Order Type: ${form.orderType === 'delivery' ? '🚗 Delivery' : form.orderType === 'drive_through' ? '🚙 Drive Through' : '🏃 Takeaway'}\n`;
    if (form.orderType === 'delivery') {
      messages += `Building/Flat: ${form.building}\n`;
      messages += `Area: ${form.area}\n`;
    } else if (form.orderType === 'drive_through') {
      messages += `Estimated Time: ${form.estimatedTime}\n`;
      messages += `Vehicle Number: ${form.vehicleNumber}\n`;
    }
    messages += `Phone: ${form.mobile}\n`;
    
    // Add payment method
    const paymentMethodDisplay = form.paymentMethod === 'cash_on_delivery' ? '💵 Cash' :
                                 form.paymentMethod === 'card' ? '💳 Card' :
                                 form.paymentMethod === 'upi' ? '📱 UPI' : 'Cash';
    messages += `Payment Method: ${paymentMethodDisplay}\n`;
    
    // Add kitchen notes if provided
    if (form.kitchenNotes && form.kitchenNotes.trim() !== "") {
      messages += `Kitchen Notes: ${form.kitchenNotes}\n`;
    }
    
    messages += "\n";

    // Add location if available, it's a delivery order, and user wants to share location
    if (form.orderType === 'delivery' && form.useCurrentLocation && location.latitude && location.longitude) {
      messages += "📍 *DELIVERY LOCATION*\n";
      messages += "━━━━━━━━━━━━━━━━━━━━\n";
      const mapUrl = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
      messages += `Location: ${mapUrl}\n\n`;
    }
    
    // Add order timestamp
    messages += "⏰ *ORDER TIME*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    messages += `Date: ${new Date().toLocaleDateString()}\n`;
    messages += `Time: ${new Date().toLocaleTimeString()}\n\n`;
    
    // Add store details link
    if (profile?.link) {
      messages += "🔗 *STORE DETAILS*\n";
      messages += "━━━━━━━━━━━━━━━━━━━━\n";
      messages += `Visit our store: ${profile.link}\n\n`;
    }
    
    messages += "Thank you! 🙏";

    // Encode the message
    const encodedMessage = encodeURIComponent(messages);

    // Construct the WhatsApp URL
    const url = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open WhatsApp Web or WhatsApp App
    window.open(url, "_blank");
    
    // Save final delivery details to localStorage when order is placed
    saveDeliveryDetailsToStorage(form);
    
    // Clear cart after placing order
    dispatch(clearCartOnOrder());
  };

  return (
    <main 
      className="min-h-screen"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
        <Back />        
        {order.length > 0 ? (
          <>
            {/* Order Type Selection */}
            <div className="mb-4 md:mb-6 lg:mb-8">
              <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6">
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-800 poppins mb-3 md:mb-4">Order Type</h3>
                <div className="flex flex-row gap-2 md:gap-3 lg:gap-4">
                  {profile?.enable_delivery && (
                    <button
                      onClick={() => changeHandler({ target: { name: 'orderType', value: 'delivery' } })}
                      className="flex-1 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl transition-all duration-300 border-2 shadow-lg transform"
                      style={form.orderType === 'delivery' ? {
                        backgroundColor: theme.colors.cartOrderTypeSelectedBg,
                        color: theme.colors.cartOrderTypeSelectedText,
                        borderColor: theme.colors.cartOrderTypeSelectedBorder,
                        transform: 'scale(1.02)'
                      } : {
                        backgroundColor: theme.colors.cartOrderTypeUnselectedBg,
                        color: theme.colors.cartOrderTypeUnselectedText,
                        borderColor: theme.colors.cartOrderTypeUnselectedBorder
                      }}
                    >
                      <div className="flex flex-col items-center space-y-1 md:space-y-2">
                        <div className="text-base md:text-lg lg:text-2xl">🚗</div>
                        <div className="font-semibold text-xs md:text-sm lg:text-base">Delivery</div>
                        <div className="text-xs md:text-sm opacity-80 text-center hidden md:block">We'll deliver to your location</div>
                      </div>
                    </button>
                  )}
                  {profile?.enable_takeaway && (
                    <button
                      onClick={() => changeHandler({ target: { name: 'orderType', value: 'takeaway' } })}
                      className="flex-1 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl transition-all duration-300 border-2 shadow-lg transform"
                      style={form.orderType === 'takeaway' ? {
                        backgroundColor: theme.colors.cartOrderTypeSelectedBg,
                        color: theme.colors.cartOrderTypeSelectedText,
                        borderColor: theme.colors.cartOrderTypeSelectedBorder,
                        transform: 'scale(1.02)'
                      } : {
                        backgroundColor: theme.colors.cartOrderTypeUnselectedBg,
                        color: theme.colors.cartOrderTypeUnselectedText,
                        borderColor: theme.colors.cartOrderTypeUnselectedBorder
                      }}
                    >
                      <div className="flex flex-col items-center space-y-1 md:space-y-2">
                        <div className="text-base md:text-lg lg:text-2xl">🏃</div>
                        <div className="font-semibold text-xs md:text-sm lg:text-base">Takeaway</div>
                        <div className="text-xs md:text-sm opacity-80 text-center hidden md:block">Pick up from our location</div>
                      </div>
                    </button>
                  )}
                  {profile?.enable_drive && (
                    <button
                      onClick={() => changeHandler({ target: { name: 'orderType', value: 'drive_through' } })}
                      className="flex-1 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl transition-all duration-300 border-2 shadow-lg transform"
                      style={form.orderType === 'drive_through' ? {
                        backgroundColor: theme.colors.cartOrderTypeSelectedBg,
                        color: theme.colors.cartOrderTypeSelectedText,
                        borderColor: theme.colors.cartOrderTypeSelectedBorder,
                        transform: 'scale(1.02)'
                      } : {
                        backgroundColor: theme.colors.cartOrderTypeUnselectedBg,
                        color: theme.colors.cartOrderTypeUnselectedText,
                        borderColor: theme.colors.cartOrderTypeUnselectedBorder
                      }}
                    >
                      <div className="flex flex-col items-center space-y-1 md:space-y-2">
                        <div className="text-base md:text-lg lg:text-2xl">🚙</div>
                        <div className="font-semibold text-xs md:text-sm lg:text-base">Drive Through</div>
                        <div className="text-xs md:text-sm opacity-80 text-center hidden md:block">Drive through our location</div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {/* left side form  */}
              <div className="col-span-1 order-1">
                <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6 xl:p-8">
                  <DeliveryForm formData={form} handleChange={changeHandler} />
                </div>
              </div>
              {/* right side  */}
              <div className="col-span-1 order-2">
                <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6 xl:p-8">
                  {/* Cart Header */}
                  <div className="flex items-center justify-between mb-3 md:mb-4 lg:mb-6 pb-2 md:pb-3 lg:pb-4 border-b border-gray-200">
                    <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 poppins">Your Cart</h2>
                    <div className="bg-primary/10 text-primary px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                      {order.length} {order.length === 1 ? 'item' : 'items'}
                    </div>
                  </div>
                  
                  {/* orders  */}
                  <div className="flex flex-col space-y-2 md:space-y-3 lg:space-y-4 max-h-72 md:max-h-80 lg:max-h-96 overflow-y-auto orderContainer mb-3 md:mb-4 lg:mb-6">
                    {order.map((item) => (
                      <OrderCard key={item.id} {...item} />
                    ))}
                  </div>
                  
                  {/* price  */}
                  <OrderPrice orderType={form.orderType} />
                  
                  {/* place order button  */}
                  <div className="mt-3 md:mt-4 lg:mt-6">
                    {disabled ? (
                      <button
                        disabled="disabled"
                        className="w-full px-3 md:px-4 lg:px-6 py-2.5 md:py-3 lg:py-4 rounded-lg md:rounded-xl bg-gray-300 text-gray-500 poppins font-semibold transition duration-500 opacity-40 cursor-not-allowed text-sm md:text-base"
                      >
                        {!storeAvailable ? 
                          (profile?.status === false ? "Store Currently Not Available" : "Store Currently Closed") :
                          form.orderType === "delivery" && form.building.trim() === "" ? "Enter Building/Flat" :
                          form.orderType === "delivery" && form.area.trim() === "" ? "Enter Area" : "Complete All Details"}
                      </button>
                    ) : (
                      <button
                        className="w-full px-3 md:px-4 lg:px-6 py-2.5 md:py-3 lg:py-4 rounded-lg md:rounded-xl poppins font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition duration-300 border-2 text-sm md:text-base"
                        style={{
                          background: `linear-gradient(to right, ${theme.colors.cartIconGradientFrom}, ${theme.colors.cartIconGradientVia}, ${theme.colors.cartIconGradientTo})`,
                          color: theme.colors.cartIconText,
                          borderColor: theme.colors.cartIconBorder
                        }}
                        onClick={() => {
                          sendMessage();
                        }}
                      >
                         Place Order Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[50vh] md:min-h-[60vh]">
            <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-4 md:p-6 lg:p-12 text-center max-w-sm md:max-w-md mx-auto">
              <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 lg:mb-6">
                <svg className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                </svg>
              </div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 poppins mb-2 md:mb-3 lg:mb-4">
                Your Cart is Empty
              </h1>
              <p className="text-gray-600 mb-3 md:mb-4 lg:mb-6 text-sm md:text-base">
                Add some delicious items to get started!
              </p>
              <button 
                onClick={() => window.history.back()}
                className="bg-primary text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold hover:bg-primary-dark transition duration-300 text-sm md:text-base"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
